import {
  Button,
  Calendar,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Popover,
  QRCode,
  Select,
  Switch,
  Tag,
  message,
} from "antd";
import type { CalendarProps } from "antd";
import {
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconFilter,
  IconInfoCircle,
  IconQrcode,
} from "@tabler/icons-react";
import dayjs, { Dayjs } from "dayjs";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CalendarEvent,
  CalendarEventInput,
  createCalendarEvent,
  deleteCalendarEvent,
  CalendarSubscription,
  fetchCalendarSubscription,
  isEventVisibleForTags,
  isMultiDay,
  listCalendarEvents,
  updateCalendarEvent,
} from "@/lib/calendar/api";
import { EventSegment, WeekLayout, layoutWeek } from "@/lib/calendar/layout";
import { orpcClient } from "@/lib/orpc-client";
import { useTranslation } from "react-i18next";

interface EmployeeTagOption {
  id: string;
  name: string;
}

interface EventFormValues {
  title: string;
  range: [Dayjs, Dayjs];
  allDay: boolean;
  description?: string;
  isCompleted: boolean;
  color?: string;
  tagIds: string[];
}

interface BarColor {
  tag: string;
  bg: string;
  border: string;
  text: string;
}

const TAG_PALETTE: BarColor[] = [
  { tag: "blue", bg: "#e6f4ff", border: "#91caff", text: "#0958d9" },
  { tag: "green", bg: "#f6ffed", border: "#b7eb8f", text: "#389e0d" },
  { tag: "orange", bg: "#fff7e6", border: "#ffd591", text: "#d46b08" },
  { tag: "purple", bg: "#f9f0ff", border: "#d3adf7", text: "#531dab" },
  { tag: "cyan", bg: "#e6fffb", border: "#87e8de", text: "#08979c" },
  { tag: "magenta", bg: "#fff0f6", border: "#ffadd2", text: "#c41d7f" },
  { tag: "gold", bg: "#fffbe6", border: "#ffe58f", text: "#d48806" },
  { tag: "geekblue", bg: "#f0f5ff", border: "#adc6ff", text: "#1d39c4" },
  { tag: "volcano", bg: "#fff2e8", border: "#ffbb96", text: "#d4380d" },
  { tag: "lime", bg: "#fcffe6", border: "#eaff8f", text: "#7cb305" },
];

const EVENT_COLOR_TAGS = ["blue", "green", "orange", "purple", "magenta"];
const EVENT_COLORS = TAG_PALETTE.filter((color) => EVENT_COLOR_TAGS.includes(color.tag));

const DEFAULT_BAR_COLOR: BarColor = {
  tag: "default",
  bg: "#f5f5f5",
  border: "#d9d9d9",
  text: "#595959",
};

function hexToRgb(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const value = parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function barColorFromHex(hex: string): BarColor | null {
  const preset = TAG_PALETTE.find(
    (color) => color.text.toLowerCase() === hex.toLowerCase()
  );
  if (preset) return preset;
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  const mix = (channel: number, weight: number) =>
    Math.round(channel * weight + 255 * (1 - weight));
  return {
    tag: hex,
    bg: `rgb(${mix(r, 0.12)}, ${mix(g, 0.12)}, ${mix(b, 0.12)})`,
    border: `rgb(${mix(r, 0.45)}, ${mix(g, 0.45)}, ${mix(b, 0.45)})`,
    text: hex,
  };
}

interface EventColorFieldProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  autoLabel: string;
}

function EventColorField({
  value,
  onChange,
  autoLabel,
}: EventColorFieldProps) {
  const swatchClass = (selected: boolean) =>
    `flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-solid p-0 transition-transform duration-150 hover:scale-110 ${
      selected ? "border-gray-800" : "border-transparent"
    }`;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="event-color-field">
      <button
        type="button"
        aria-label={autoLabel}
        aria-pressed={!value}
        data-testid="event-color-auto"
        onClick={() => onChange?.(undefined)}
        className={`flex h-8 cursor-pointer items-center gap-1 rounded-full border-2 border-solid px-3 text-xs transition-colors ${
          !value
            ? "border-gray-800 bg-gray-100 text-gray-800"
            : "border-dashed border-gray-300 bg-white text-gray-500 hover:border-gray-400"
        }`}
      >
        {!value ? <IconCheck size={14} /> : null}
        {autoLabel}
      </button>
      {EVENT_COLORS.map((color) => {
        const selected = value?.toLowerCase() === color.text.toLowerCase();
        return (
          <button
            key={color.tag}
            type="button"
            aria-label={color.tag}
            aria-pressed={selected}
            data-testid={`event-color-${color.tag}`}
            onClick={() => onChange?.(color.text)}
            className={swatchClass(selected)}
          >
            <span
              className="flex h-6 w-6 items-center justify-center rounded-full text-white"
              style={{ background: color.text }}
            >
              {selected ? <IconCheck size={14} /> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const BAR_HEIGHT = 22;
const BAR_GAP = 3;
const BAR_INSET = 4;
const MIN_ROW_HEIGHT = 64;
const DATE_ROW_HEIGHT = 28;
const CELL_CHROME_HEIGHT = 6;

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";
const DATETIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
const DEFAULT_START_HOUR = 9;

function CompanyCalendarPage() {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [form] = Form.useForm<EventFormValues>();
  const allDay = Form.useWatch("allDay", form);

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [tags, setTags] = useState<EmployeeTagOption[]>([]);
  const [filterTagIds, setFilterTagIds] = useState<string[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [saving, setSaving] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscription, setSubscription] =
    useState<CalendarSubscription | null>(null);
  const subscribeUrl = subscription?.icsUrl ?? null;
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [viewDate, setViewDate] = useState<Dayjs>(() => dayjs());
  const calendarBoxRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState(MIN_ROW_HEIGHT);
  const [moreDate, setMoreDate] = useState<string | null>(null);

  useLayoutEffect(() => {
    const box = calendarBoxRef.current;
    if (!box) return;
    const measure = () => {
      const calendar = box.firstElementChild as HTMLElement | null;
      const rows = box.querySelectorAll<HTMLElement>(
        ".ant-picker-content tbody tr"
      );
      if (!calendar || rows.length === 0) return;
      let rowsHeight = 0;
      rows.forEach((row) => {
        rowsHeight += row.offsetHeight;
      });
      const chrome = calendar.offsetHeight - rowsHeight;
      const available = box.clientHeight - chrome;
      setRowHeight(
        Math.max(MIN_ROW_HEIGHT, Math.floor(available / rows.length))
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  const reloadEvents = useCallback(async () => {
    try {
      setEvents(await listCalendarEvents());
    } catch (error) {
      console.error(error);
      message.error(tRef.current("companyCalendar.loadFailed"));
    }
  }, []);

  useEffect(() => {
    reloadEvents();
    orpcClient.employeeTag
      .list({})
      .then((data: any) => {
        setTags((data ?? []).map((tag: any) => ({ id: tag.id, name: tag.name })));
      })
      .catch((error: unknown) => {
        console.error(error);
      });
  }, [reloadEvents]);

  const tagById = useMemo(() => {
    const map = new Map<string, { name: string; color: BarColor }>();
    tags.forEach((tag, index) =>
      map.set(tag.id, {
        name: tag.name,
        color: TAG_PALETTE[index % TAG_PALETTE.length],
      })
    );
    return map;
  }, [tags]);

  const visibleEvents = useMemo(
    () => events.filter((event) => isEventVisibleForTags(event, filterTagIds)),
    [events, filterTagIds]
  );

  const getWeekLayout = useMemo(() => {
    const cache = new Map<string, WeekLayout>();
    return (date: Dayjs) => {
      const weekStart = date.startOf("week");
      const key = weekStart.format(DATE_FORMAT);
      let layout = cache.get(key);
      if (!layout) {
        const weekDates = Array.from({ length: 7 }, (_, i) =>
          weekStart.add(i, "day").format(DATE_FORMAT)
        );
        layout = layoutWeek(weekDates, visibleEvents);
        cache.set(key, layout);
      }
      return layout;
    };
  }, [visibleEvents]);

  const tagOptions = tags.map((tag) => ({ value: tag.id, label: tag.name }));

  const openCreate = (date: Dayjs) => {
    setEditingEvent(null);
    form.resetFields();
    form.setFieldsValue({
      title: "",
      range: [date.startOf("day"), date.startOf("day")],
      allDay: true,
      description: "",
      isCompleted: false,
      color: undefined,
      tagIds: [],
    });
    setEditorOpen(true);
  };

  const openEdit = (event: CalendarEvent) => {
    setEditingEvent(event);
    form.resetFields();
    form.setFieldsValue({
      title: event.title,
      range: [
        dayjs(
          `${event.startDate} ${event.startTime ?? "00:00"}`,
          DATETIME_FORMAT
        ),
        dayjs(`${event.endDate} ${event.endTime ?? "00:00"}`, DATETIME_FORMAT),
      ],
      allDay: event.allDay,
      description: event.description ?? "",
      isCompleted: event.isCompleted,
      color: event.color,
      tagIds: event.tagIds,
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingEvent(null);
  };

  const handleSave = async () => {
    let values: EventFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const [start, end] = values.range;
    const input: CalendarEventInput = {
      title: values.title.trim(),
      startDate: start.format(DATE_FORMAT),
      endDate: end.format(DATE_FORMAT),
      allDay: values.allDay,
      startTime: values.allDay ? undefined : start.format(TIME_FORMAT),
      endTime: values.allDay ? undefined : end.format(TIME_FORMAT),
      description: values.description?.trim() || undefined,
      isCompleted: values.isCompleted ?? false,
      color: values.color || undefined,
      style: editingEvent?.style ?? null,
      tagIds: values.tagIds ?? [],
    };
    setSaving(true);
    try {
      if (editingEvent) {
        await updateCalendarEvent(editingEvent.id, input);
        message.success(t("companyCalendar.updateSuccess"));
      } else {
        await createCalendarEvent(input);
        message.success(t("companyCalendar.createSuccess"));
      }
      await reloadEvents();
      closeEditor();
    } catch (error) {
      console.error(error);
      message.error(t("companyCalendar.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingEvent) return;
    try {
      await deleteCalendarEvent(editingEvent.id);
      message.success(t("companyCalendar.deleteSuccess"));
      await reloadEvents();
      closeEditor();
    } catch (error) {
      console.error(error);
      message.error(t("companyCalendar.deleteFailed"));
    }
  };

  const openSubscribe = async () => {
    setSubscribeOpen(true);
    setSubscribeLoading(true);
    try {
      setSubscription(await fetchCalendarSubscription());
    } catch (error) {
      console.error(error);
      setSubscription(null);
    } finally {
      setSubscribeLoading(false);
    }
  };

  const copySubscribeUrl = async () => {
    if (!subscribeUrl) return;
    try {
      await navigator.clipboard.writeText(subscribeUrl);
      message.success(t("companyCalendar.copied"));
    } catch {
      message.error(t("companyCalendar.copyFailed"));
    }
  };

  const handleAllDayChange = (checked: boolean) => {
    const range = form.getFieldValue("range") as EventFormValues["range"];
    if (checked || !range) return;
    const [start, end] = range;
    const untimed =
      start.format(TIME_FORMAT) === "00:00" && end.format(TIME_FORMAT) === "00:00";
    if (!untimed) return;
    const nextStart = start.hour(DEFAULT_START_HOUR).minute(0);
    const nextEnd = end.isSame(start, "day")
      ? nextStart.add(1, "hour")
      : end.hour(DEFAULT_START_HOUR + 1).minute(0);
    form.setFieldsValue({ range: [nextStart, nextEnd] });
  };

  const onSelect: CalendarProps<Dayjs>["onSelect"] = (date, info) => {
    if (info.source === "date") openCreate(date);
  };

  const eventColor = (event: CalendarEvent): BarColor =>
    (event.color ? barColorFromHex(event.color) : null) ??
    event.tagIds.map((id) => tagById.get(id)?.color).find(Boolean) ??
    DEFAULT_BAR_COLOR;

  const renderSegment = (segment: EventSegment, date: string) => {
    const { event, isStart, isEnd, showLabel, labelSpan } = segment;
    const multiDay = isMultiDay(event);
    const color = eventColor(event);
    const timePrefix =
      !event.allDay && isStart && event.startTime ? `${event.startTime} ` : "";
    const rangeLabel = multiDay
      ? `${event.startDate.slice(5)}${
          !event.allDay && event.startTime ? ` ${event.startTime}` : ""
        } ~ ${event.endDate.slice(5)}${
          !event.allDay && event.endTime ? ` ${event.endTime}` : ""
        }`
      : !event.allDay && event.startTime
      ? `${event.startTime} ~ ${event.endTime ?? ""}`
      : "";
    const span = !multiDay ? "single" : isStart ? "start" : isEnd ? "end" : "middle";
    return (
      <button
        key={`${event.id}-${date}`}
        type="button"
        data-testid={`calendar-event-${event.id}`}
        data-span={span}
        data-lane={segment.lane}
        title={rangeLabel ? `${event.title}（${rangeLabel}）` : event.title}
        onClick={(e) => {
          e.stopPropagation();
          openEdit(event);
        }}
        className="relative block cursor-pointer p-0 text-left text-xs"
        style={{
          height: BAR_HEIGHT,
          marginLeft: isStart ? BAR_INSET : 0,
          marginRight: isEnd ? BAR_INSET : 0,
          width: `calc(100% - ${(isStart ? BAR_INSET : 0) + (isEnd ? BAR_INSET : 0)}px)`,
          background: color.bg,
          color: color.text,
          borderStyle: "solid",
          borderColor: color.border,
          borderWidth: `1px ${isEnd ? 1 : 0}px 1px ${isStart ? 1 : 0}px`,
          borderRadius: `${isStart ? 4 : 0}px ${isEnd ? 4 : 0}px ${isEnd ? 4 : 0}px ${
            isStart ? 4 : 0
          }px`,
        }}
      >
        {showLabel ? (
          <span
            className={`pointer-events-none absolute left-0 top-0 z-10 truncate px-1.5 text-left font-medium ${
              event.isCompleted ? "line-through opacity-60" : ""
            }`}
            style={{
              lineHeight: `${BAR_HEIGHT - 2}px`,
              width: `calc(${labelSpan * 100}% - ${BAR_INSET * 2}px)`,
            }}
          >
            {!isStart ? "◂ " : ""}
            {timePrefix}
            {event.title}
          </span>
        ) : null}
      </button>
    );
  };

  const laneCapacity = Math.max(
    1,
    Math.floor(
      (rowHeight - CELL_CHROME_HEIGHT - DATE_ROW_HEIGHT + BAR_GAP) /
        (BAR_HEIGHT + BAR_GAP)
    )
  );

  const renderMoreList = (day: Dayjs, dayEvents: CalendarEvent[]) => (
    <div
      className="flex w-60 flex-col gap-1 p-3"
      data-testid={`calendar-more-list-${day.format(DATE_FORMAT)}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-1 text-sm font-semibold text-gray-700">
        {day.format("YYYY-MM-DD (dd)")}
      </div>
      {dayEvents.map((event) => {
        const color = eventColor(event);
        return (
          <button
            key={event.id}
            type="button"
            onClick={() => {
              setMoreDate(null);
              openEdit(event);
            }}
            className={`cursor-pointer truncate rounded border border-solid px-2 py-1.5 text-left text-xs ${
              event.isCompleted ? "line-through opacity-60" : ""
            }`}
            style={{ background: color.bg, color: color.text, borderColor: color.border }}
          >
            {!event.allDay && event.startTime ? `${event.startTime} ` : ""}
            {event.title}
            {isMultiDay(event)
              ? `（${event.startDate.slice(5)} ~ ${event.endDate.slice(5)}）`
              : ""}
          </button>
        );
      })}
    </div>
  );

  const fullCellRender: CalendarProps<Dayjs>["fullCellRender"] = (
    current,
    info
  ) => {
    if (info.type !== "date") return info.originNode;
    const date = current.format(DATE_FORMAT);
    const weekLayout = getWeekLayout(current);
    const slots = weekLayout.get(date) ?? [];
    let weekLanes = 0;
    weekLayout.forEach((day) => {
      weekLanes = Math.max(weekLanes, day.length);
    });
    const shownLanes =
      weekLanes > laneCapacity ? laneCapacity - 1 : laneCapacity;
    const shown = slots.slice(0, shownLanes);
    const hidden = slots
      .slice(shownLanes)
      .filter((segment): segment is EventSegment => segment !== null);
    const inMonth = current.isSame(viewDate, "month");
    const isToday = current.isSame(dayjs(), "day");
    return (
      <div
        data-testid={`calendar-cell-${date}`}
        style={{ height: rowHeight }}
        data-today={isToday ? "true" : undefined}
        className={`group flex cursor-pointer flex-col overflow-hidden border-0 border-t-2 border-solid border-gray-100 pb-1 text-left transition-colors duration-150 hover:bg-blue-50/70 active:bg-blue-100/70 ${
          isToday ? "bg-blue-50/40" : inMonth ? "" : "bg-gray-50/60"
        }`}
      >
        <div
          style={{ height: DATE_ROW_HEIGHT }}
          className="flex shrink-0 items-center justify-end px-1.5"
        >
          <span
            data-testid={`calendar-date-${date}`}
            className={`flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-sm transition-colors duration-150 ${
              isToday
                ? "bg-blue-600 font-semibold text-white"
                : inMonth
                ? "text-gray-700 group-hover:bg-blue-100 group-hover:text-blue-700"
                : "text-gray-300 group-hover:bg-gray-200 group-hover:text-gray-500"
            }`}
          >
            {current.date()}
          </span>
        </div>
        <div className="flex flex-col" style={{ gap: BAR_GAP }}>
          {shown.map((segment, lane) =>
            segment ? (
              renderSegment(segment, date)
            ) : (
              <div key={`empty-${lane}`} style={{ height: BAR_HEIGHT }} />
            )
          )}
          {hidden.length > 0 ? (
            <Popover
              trigger="click"
              open={moreDate === date}
              onOpenChange={(open) => setMoreDate(open ? date : null)}
              styles={{ body: { padding: 0 } }}
              content={renderMoreList(
                current,
                slots
                  .filter((segment): segment is EventSegment => segment !== null)
                  .map((segment) => segment.event)
              )}
            >
              <button
                type="button"
                data-testid={`calendar-more-${date}`}
                onClick={(e) => e.stopPropagation()}
                className="mx-1 cursor-pointer rounded border-0 bg-transparent px-1.5 text-left text-xs font-medium text-gray-500 hover:bg-gray-100"
                style={{ height: BAR_HEIGHT }}
              >
                {t("companyCalendar.moreEvents", { count: hidden.length })}
              </button>
            </Popover>
          ) : null}
        </div>
      </div>
    );
  };

  const headerRender: CalendarProps<Dayjs>["headerRender"] = ({
    value,
    onChange,
  }) => (
    <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
      <div className="flex items-center gap-2">
        <Button
          size="large"
          icon={<IconChevronLeft size={18} />}
          aria-label={t("companyCalendar.prevMonth")}
          onClick={() => onChange(value.subtract(1, "month"))}
        />
        <Button
          size="large"
          icon={<IconChevronRight size={18} />}
          aria-label={t("companyCalendar.nextMonth")}
          onClick={() => onChange(value.add(1, "month"))}
        />
        <Button size="large" onClick={() => onChange(dayjs())}>
          {t("companyCalendar.today")}
        </Button>
        <div
          className="ml-2 flex items-center gap-2 text-xl font-semibold text-gray-700"
          data-testid="calendar-month-label"
        >
          {value.format("YYYY-MM")}
        </div>
        <span className="ml-2 hidden text-xs text-gray-400 xl:inline">
          {t("companyCalendar.clickHint")}
        </span>
      </div>
      <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
        <IconFilter size={16} className="shrink-0 text-gray-500" />
        <Select
          mode="multiple"
          allowClear
          size="large"
          maxTagCount="responsive"
          className="w-full max-w-sm"
          placeholder={t("companyCalendar.filterPlaceholder")}
          value={filterTagIds}
          onChange={setFilterTagIds}
          options={tagOptions}
          optionFilterProp="label"
          data-testid="calendar-tag-filter"
        />
        <Button
          size="large"
          icon={<IconQrcode size={18} />}
          onClick={openSubscribe}
          data-testid="calendar-subscribe-button"
        >
          {t("companyCalendar.subscribe")}
        </Button>
      </div>
    </div>
  );

  const renderTagList = (tagIds: string[]) =>
    tagIds.length === 0 ? (
      <Tag>{t("companyCalendar.allEmployees")}</Tag>
    ) : (
      tagIds.map((id) => {
        const tag = tagById.get(id);
        return (
          <Tag key={id} color={tag?.color.tag}>
            {tag?.name ?? t("companyCalendar.unknownTag")}
          </Tag>
        );
      })
    );

  return (
    <div className="flex h-full min-h-[560px] flex-col p-3 md:p-4">
      {/* <div className="mb-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("companyCalendar.title")}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("companyCalendar.description")}
          </div>
        </div>
      </div> */}
      <div className="flex min-h-0 flex-1 flex-col rounded-xl bg-white p-3 shadow-sm md:p-4">
        <div
          ref={calendarBoxRef}
          data-testid="calendar-box"
          className="min-h-0 flex-1 overflow-auto"
        >
          <Calendar
            className="[&_.ant-picker-cell]:align-top"
            mode="month"
            value={viewDate}
            onChange={setViewDate}
            onSelect={onSelect}
            fullCellRender={fullCellRender}
            headerRender={headerRender}
          />
        </div>
      </div>

      <Modal
        open={editorOpen}
        title={
          editingEvent
            ? t("companyCalendar.editEvent")
            : t("companyCalendar.addEvent")
        }
        width={880}
        onCancel={closeEditor}
        forceRender
        footer={[
          editingEvent ? (
            <Popconfirm
              key="delete"
              title={t("companyCalendar.deleteConfirm")}
              okText={t("companyCalendar.delete")}
              cancelText={t("companyCalendar.cancel")}
              okButtonProps={{ danger: true }}
              onConfirm={handleDelete}
            >
              <Button danger size="large" className="float-left">
                {t("companyCalendar.delete")}
              </Button>
            </Popconfirm>
          ) : null,
          <Button key="cancel" size="large" onClick={closeEditor}>
            {t("companyCalendar.cancel")}
          </Button>,
          <Button
            key="save"
            size="large"
            type="primary"
            loading={saving}
            onClick={handleSave}
          >
            {t("companyCalendar.save")}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark
          className="grid grid-cols-1 gap-x-6 pt-2 md:grid-cols-2"
        >
          <Form.Item
            name="title"
            label={t("companyCalendar.eventTitle")}
            className="md:col-span-2"
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("companyCalendar.titleRequired"),
              },
            ]}
          >
            <Input
              size="large"
              placeholder={t("companyCalendar.eventTitlePlaceholder")}
              maxLength={100}
            />
          </Form.Item>
          <Form.Item
            name="range"
            label={
              allDay === false
                ? t("companyCalendar.dateTimeRange")
                : t("companyCalendar.dateRange")
            }
            extra={t("companyCalendar.dateRangeHint")}
            rules={[
              { required: true, message: t("companyCalendar.dateRequired") },
              {
                validator: (_, value?: [Dayjs, Dayjs]) =>
                  !value || allDay !== false || value[1].isAfter(value[0])
                    ? Promise.resolve()
                    : Promise.reject(new Error(t("companyCalendar.endBeforeStart"))),
              },
            ]}
          >
            <DatePicker.RangePicker
              size="large"
              className="w-full"
              format={allDay === false ? DATETIME_FORMAT : DATE_FORMAT}
              showTime={
                allDay === false
                  ? { format: TIME_FORMAT, minuteStep: 5 }
                  : false
              }
              inputReadOnly
            />
          </Form.Item>
          <Form.Item
            name="tagIds"
            label={t("companyCalendar.visibleTags")}
            extra={t("companyCalendar.visibleTagsHint")}
          >
            <Select
              mode="multiple"
              allowClear
              size="large"
              placeholder={t("companyCalendar.allEmployees")}
              options={tagOptions}
              optionFilterProp="label"
              data-testid="event-tag-select"
            />
          </Form.Item>
          <div className="flex items-end gap-8">
            <Form.Item
              name="allDay"
              label={t("companyCalendar.allDay")}
              valuePropName="checked"
            >
              <Switch onChange={handleAllDayChange} />
            </Form.Item>
            <Form.Item name="isCompleted" valuePropName="checked">
              <Checkbox data-testid="event-completed-checkbox">
                {t("companyCalendar.isCompleted")}
              </Checkbox>
            </Form.Item>
          </div>
          <Form.Item
            name="color"
            label={t("companyCalendar.color")}
            extra={t("companyCalendar.colorHint")}
          >
            <EventColorField autoLabel={t("companyCalendar.colorAuto")} />
          </Form.Item>
          {/* <Form.Item name="location" label={t("companyCalendar.location")}>
            <Input size="large" maxLength={100} />
          </Form.Item> */}
          <Form.Item
            name="description"
            label={t("companyCalendar.eventDescription")}
            className="md:col-span-2"
          >
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          {editingEvent &&
          (editingEvent.createdByName || editingEvent.updatedByName) ? (
            <div className="md:col-span-2">
              {/* <div className="flex flex-wrap items-center gap-1">
                {t("companyCalendar.currentlyVisibleTo")}
                {renderTagList(editingEvent.tagIds)}
              </div> */}
              <Popover
                trigger={["hover", "click"]}
                placement="topLeft"
                content={
                  <div className="flex flex-col gap-1 text-xs text-gray-600">
                    {editingEvent.createdByName ? (
                      <div data-testid="event-created-by">
                        {t("companyCalendar.createdBy")}
                        {editingEvent.createdByName}
                        {editingEvent.createdAt
                          ? `（${dayjs(editingEvent.createdAt).format(DATETIME_FORMAT)}）`
                          : ""}
                      </div>
                    ) : null}
                    {editingEvent.updatedByName ? (
                      <div data-testid="event-updated-by">
                        {t("companyCalendar.updatedBy")}
                        {editingEvent.updatedByName}
                        {editingEvent.updatedAt
                          ? `（${dayjs(editingEvent.updatedAt).format(DATETIME_FORMAT)}）`
                          : ""}
                      </div>
                    ) : null}
                  </div>
                }
              >
                <button
                  type="button"
                  data-testid="event-audit-trigger"
                  className="flex min-h-[32px] cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-xs text-gray-400 hover:text-gray-600"
                >
                  <IconInfoCircle size={14} />
                  {t("companyCalendar.auditInfo")}
                </button>
              </Popover>
            </div>
          ) : null}
        </Form>
      </Modal>

      <Modal
        open={subscribeOpen}
        title={t("companyCalendar.subscribeTitle")}
        onCancel={() => setSubscribeOpen(false)}
        footer={[
          <Button
            key="copy"
            size="large"
            disabled={!subscribeUrl}
            onClick={copySubscribeUrl}
          >
            {t("companyCalendar.copyLink")}
          </Button>,
          <Button
            key="close"
            size="large"
            type="primary"
            onClick={() => setSubscribeOpen(false)}
          >
            {t("companyCalendar.close")}
          </Button>,
        ]}
      >
        <div className="flex flex-col items-center gap-4 py-4">
          <QRCode
            data-testid="calendar-subscribe-qrcode"
            value={subscribeUrl ?? "-"}
            size={220}
            status={
              subscribeLoading ? "loading" : subscribeUrl ? "active" : "expired"
            }
            statusRender={(info) =>
              info.status === "expired" ? (
                <div className="px-6 text-center text-sm text-gray-600">
                  {t("companyCalendar.subscribeUnavailable")}
                </div>
              ) : (
                info.status === "loading" && (
                  <div className="text-sm text-gray-500">
                    {t("companyCalendar.loading")}
                  </div>
                )
              )
            }
          />
          <div className="text-center text-sm text-gray-600">
            {t("companyCalendar.subscribeHint")}
          </div>
          {/* {filterTagIds.length > 0 ? (
            <div className="flex flex-wrap items-center justify-center gap-1 text-xs text-gray-500">
              {t("companyCalendar.subscribeScope")}
              {renderTagList(filterTagIds)}
            </div>
          ) : null} */}
          {subscription ? (
            <div className="flex w-full flex-wrap justify-center gap-2">
              <Button
                size="large"
                href={subscription.googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calendar-subscribe-google"
              >
                {t("companyCalendar.subscribeGoogle")}
              </Button>
              <Button
                size="large"
                href={subscription.webcalUrl}
                data-testid="calendar-subscribe-apple"
              >
                {t("companyCalendar.subscribeApple")}
              </Button>
            </div>
          ) : null}
          {subscribeUrl ? (
            <div className="w-full break-all rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
              {subscribeUrl}
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}

export default CompanyCalendarPage;
